import type { HTMLAttributes, PropsWithChildren, Ref } from "react";

import { cn } from "@/ui/lib";
import { DefinedDefaults, TWithDataComponent } from "@/ui/lib/types";
import { TSeparatorProps } from "@/ui/components/primitives/Separator";
import {
  TTextSubtitleProps,
  TTextSubtitleSize,
} from "../../typography/TextSubtitle";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const ITEM_PADDING_CLASS_MAP = {
  default: "p-4",
  none: "",
};

export const ITEM_GROUP_CLASS = "flex w-full flex-col";

export const ITEM_CLASS = cn(
  "group/item relative flex w-full flex-wrap items-start gap-4",
  "rounded-sys-xs text-left outline-none transition-colors",
  "[&:is(a,button)]:cursor-pointer [&:is(a,button)]:hover:bg-surface-subtle",
  "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-primary",
);

export const ITEM_SIZE_CLASS_MAP: Record<TItemSize, string> = {
  default: "py-4",
  sm: "py-2",
};

export const ITEM_VARIANT_CLASS_MAP = {
  default: "",
  outline: "border border-solid border-muted",
  eyebrow: "",
} as const;

export const ITEM_MEDIA_CLASS = "flex shrink-0 items-center justify-center text-foreground";

export const ITEM_MEDIA_VARIANT_CLASS_MAP = {
  default: "[&_svg]:size-6",
  icon: "size-8 rounded-sys-full bg-surface-subtle [&_svg]:size-6",
  image: "size-10 overflow-hidden rounded-sys-sm [&_img]:size-full [&_img]:object-cover",
} as const;

export const ITEM_CONTENT_CLASS =
  "flex min-w-0 flex-1 flex-col justify-center gap-1 self-stretch";

export const ITEM_ACTIONS_CLASS =
  "flex shrink-0 items-center justify-end gap-2 self-stretch";

export const ITEM_HEADER_CLASS = "flex basis-full items-center justify-between gap-2";

export const ITEM_FOOTER_CLASS = "flex basis-full items-center justify-between gap-2";

export const ITEM_TITLE_SIZE_PROP_MAP: Record<TItemSize, TTextSubtitleSize> = {
  default: 2,
  sm: 4,
};

/*-------------------------------------
|  Types
-------------------------------------*/

export type TItemGroupProps = HTMLAttributes<HTMLDivElement> & TWithDataComponent;

export type TItemPadding = keyof typeof ITEM_PADDING_CLASS_MAP;
export type TItemVariant = keyof typeof ITEM_VARIANT_CLASS_MAP;
export type TItemSize = "default" | "sm";

export type TItemProps = HTMLAttributes<HTMLDivElement> & {
  variant?: TItemVariant;
  size?: TItemSize;
  /**
   * Render the Item as its single child element (e.g. an `<a>` or `<button>`)
   * instead of a `<div>`, merging Item's styles/attributes onto it. Use when the
   * whole row should be a link or button so it keeps native semantics, focus and
   * keyboard behaviour.
   */
  asChild?: boolean;
  padding?: TItemPadding;
} & TWithDataComponent;

export type TItemChildProps = PropsWithChildren & {
  className?: string;
  "data-component"?: string;
  ref?: Ref<HTMLElement>;
};

export type TItemMediaVariant = keyof typeof ITEM_MEDIA_VARIANT_CLASS_MAP;

export type TItemMediaProps = HTMLAttributes<HTMLDivElement> & {
  variant?: TItemMediaVariant;
};

export type TItemContentProps = HTMLAttributes<HTMLDivElement>;
export type TItemTitleProps = HTMLAttributes<HTMLHeadingElement> &
  Pick<TItemProps, "size"> &
  Pick<TTextSubtitleProps, "tag">;
export type TItemDescriptionProps = HTMLAttributes<HTMLParagraphElement>;
export type TItemActionsProps = HTMLAttributes<HTMLDivElement>;
export type TItemHeaderProps = HTMLAttributes<HTMLDivElement>;
export type TItemFooterProps = HTMLAttributes<HTMLDivElement>;
export type TItemSeparatorProps = TSeparatorProps;

type TItemGroupDefaultProps = DefinedDefaults<
  TItemGroupProps,
  "data-component"
>;
type TItemDefaultProps = DefinedDefaults<
  TItemProps,
  "data-component" | "variant" | "size" | "asChild" | "padding"
>;
type TItemMediaDefaultProps = DefinedDefaults<TItemMediaProps, "variant">;
type TItemTitleDefaultProps = DefinedDefaults<TItemTitleProps, "size" | "tag">;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const ITEM_GROUP_DEFAULTS = {
  "data-component": "ItemGroup",
} satisfies TItemGroupDefaultProps;

export const ITEM_DEFAULTS = {
  "data-component": "Item",
  variant: "default",
  size: "default",
  asChild: false,
  padding: "default",
} satisfies TItemDefaultProps;

export const ITEM_MEDIA_DEFAULTS = {
  variant: "default",
} satisfies TItemMediaDefaultProps;

export const ITEM_TITLE_DEFAULTS = {
  size: "default",
  tag: "h3",
} satisfies TItemTitleDefaultProps;
